import { useState, useEffect, useRef } from 'react';

type Role = 'bee' | 'tiger' | 'chain';
type Phase = 'roleSelect' | 'playing' | 'result';

interface Situation {
  id: string;
  character: 'bee' | 'tiger';
  question: string;
  options: string[];
  correct: number;
  correctFeedback: string;
  wrongFeedback: string;
}

const SITUATIONS: Situation[] = [
  {
    id: 's1', character: 'bee',
    question: 'Con ong hỏi: "Để lấy mật ngon nhất, đàn ong cần làm gì?"',
    options: ['Bay lung tung không có kế hoạch', 'Làm việc theo nhóm có tổ chức', 'Chỉ để con ong chúa làm việc', 'Không cần làm gì'],
    correct: 1,
    correctFeedback: 'Đúng rồi! Con ong hài lòng — đàn người được đi tiếp!',
    wrongFeedback: 'Sai rồi! Nhưng con ong vẫn cho đi vì bạn đã cố gắng!'
  },
  {
    id: 's2', character: 'bee',
    question: 'Con ong hỏi: "Người Bahnar giữ gìn văn hóa bằng cách nào?"',
    options: ['Quên đi và học theo hiện đại', 'Truyền dạy cho con cháu', 'Chỉ viết vào sách vở', 'Không cần giữ gìn'],
    correct: 1,
    correctFeedback: 'Chính xác! Con ong vui mừng và chúc mừng đàn người!',
    wrongFeedback: 'Chưa đúng, nhưng con ong vẫn mỉm cười và cho đi tiếp!'
  },
  {
    id: 's3', character: 'tiger',
    question: 'Con hổ gầm: "Đoàn người dám đi qua lãnh thổ của ta! Nhưng ta thử thách: Cồng chiêng của người Bahnar có bao nhiêu chiếc?',
    options: ['3-5 chiếc', '6-8 chiếc', '1-2 chiếc', '10 chiếc trở lên'],
    correct: 1,
    correctFeedback: 'Con hổ gật đầu tôn trọng, nhưng vẫn cố tách đuôi đàn người!',
    wrongFeedback: 'Con hổ cười khoái trá và cố tách đuôi đàn người!'
  },
  {
    id: 's4', character: 'tiger',
    question: 'Con hổ hỏi: "Nhà rông của người Bahnar có đặc điểm gì đặc biệt?"',
    options: ['Mái rất thấp và nhỏ bé', 'Mái cao vút và rộng rãi cho cả làng', 'Làm bằng bê tông kiên cố', 'Không có cửa ra vào'],
    correct: 1,
    correctFeedback: 'Đúng vậy! Con hổ vẫn truy đuổi nhưng người đầu bảo vệ được đuôi!',
    wrongFeedback: 'Sai! Con hổ tấn công mạnh hơn, người đầu phải cố sức bảo vệ!'
  },
];

function saveAchievement(data: object) {
  try {
    const key = 'bahnar_achievements';
    const existing = JSON.parse(localStorage.getItem(key) || '[]');
    existing.unshift({ ...data, date: new Date().toLocaleDateString('vi-VN') });
    localStorage.setItem(key, JSON.stringify(existing.slice(0, 50)));
  } catch (_) { /* ignore */ }
}

function useGameMusic(playing: boolean) {
  const ctxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!playing) {
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null; }
      if (intervalRef.current) { clearInterval(intervalRef.current); intervalRef.current = null; }
      return;
    }
    const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    const ctx = new AudioCtx();
    ctxRef.current = ctx;
    const master = ctx.createGain();
    master.gain.value = 0.07;
    master.connect(ctx.destination);
    const notes = [220, 261.63, 293.66, 349.23, 440];
    const pattern = [2, 4, 3, 1, 0, 2, 4, 1, 3, 0, 2, 4];
    let idx = 0;
    const scheduleNote = () => {
      if (!ctxRef.current) return;
      const osc = ctx.createOscillator();
      const g = ctx.createGain();
      osc.connect(g); g.connect(master);
      osc.type = 'triangle';
      osc.frequency.value = notes[pattern[idx % pattern.length]];
      const t = ctx.currentTime;
      g.gain.setValueAtTime(0.5, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + 0.4);
      osc.start(t); osc.stop(t + 0.45);
      idx++;
    };
    intervalRef.current = setInterval(scheduleNote, 550);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (ctxRef.current) { ctxRef.current.close(); ctxRef.current = null; }
    };
  }, [playing]);
}

export default function ConOngGame({ onBack }: { onBack: () => void }) {
  const [phase, setPhase] = useState<Phase>('roleSelect');
  const [role, setRole] = useState<Role | null>(null);
  const [situationIdx, setSituationIdx] = useState(0);
  const [score, setScore] = useState(0);
  const [chainLength, setChainLength] = useState(5);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [answered, setAnswered] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [saved, setSaved] = useState(false);

  useGameMusic(phase === 'playing');

  const currentSituation = SITUATIONS[situationIdx % SITUATIONS.length];

  const startGame = (selectedRole: Role) => {
    setRole(selectedRole);
    setPhase('playing');
  };

  const handleAnswer = (idx: number) => {
    if (answered) return;
    setSelectedAnswer(idx);
    setAnswered(true);
    const correct = idx === currentSituation.correct;
    if (correct) {
      setScore(s => s + 10);
    } else {
      if (currentSituation.character === 'tiger') setChainLength(l => Math.max(1, l - 1));
    }
    setFeedback(correct ? currentSituation.correctFeedback : currentSituation.wrongFeedback);

    setTimeout(() => {
      setSelectedAnswer(null);
      setAnswered(false);
      setFeedback('');
      if (situationIdx >= SITUATIONS.length - 1) {
        setPhase('result');
      } else {
        setSituationIdx(p => p + 1);
      }
    }, 2000);
  };

  const handleSave = () => {
    saveAchievement({ game: 'Con ong', role: role === 'bee' ? 'Con ong' : role === 'tiger' ? 'Con hổ' : 'Đoàn người', score, chainLength });
    setSaved(true);
  };

  if (phase === 'roleSelect') {
    return (
      <div className="min-h-screen bg-primary-cream flex items-center justify-center px-6 py-12">
        <div className="max-w-2xl w-full">
          <h2 className="text-3xl font-extrabold text-primary-brown-dark text-center mb-3">Trò chơi Con ong</h2>
          <p className="text-primary-brown-light text-center mb-8">Chọn vai của bạn để bắt đầu cuộc phiêu lưu!</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { r: 'bee' as Role, emoji: '🐝', label: 'Con ong', desc: 'Bảo vệ tổ và ra câu hỏi thử thách đàn người đến gần', color: 'border-amber-400 bg-amber-50', btn: 'from-amber-400 to-amber-300' },
              { r: 'tiger' as Role, emoji: '🐯', label: 'Con hổ', desc: 'Ra câu hỏi và cố tách đuôi đàn người — dù đúng hay sai!', color: 'border-orange-400 bg-orange-50', btn: 'from-orange-500 to-orange-400' },
              { r: 'chain' as Role, emoji: '👫', label: 'Đoàn người', desc: 'Bám chặt vào nhau, giải quyết tình huống và đi qua mọi trở ngại', color: 'border-sky-400 bg-sky-50', btn: 'from-sky-500 to-sky-400' },
            ].map(({ r, emoji, label, desc, color, btn }) => (
              <button key={r} onClick={() => startGame(r)}
                className={`flex flex-col items-center gap-3 p-7 rounded-3xl border-2 cursor-pointer hover:scale-105 transition-all ${color}`}>
                <span className="text-6xl">{emoji}</span>
                <span className="text-xl font-extrabold text-primary-brown-dark">{label}</span>
                <p className="text-sm text-primary-brown-light text-center leading-relaxed">{desc}</p>
                <span className={`mt-2 px-5 py-2 bg-gradient-to-r ${btn} text-white font-bold rounded-full text-sm whitespace-nowrap`}>
                  Chọn vai này
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (phase === 'result') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-primary-yellow/20 to-primary-cream flex flex-col items-center justify-center px-6 py-12">
        <div className="text-7xl mb-4">🏁</div>
        <h2 className="text-3xl font-extrabold text-primary-brown-dark mb-2">Trò chơi kết thúc!</h2>
        <p className="text-primary-brown-light mb-2">Vai: {role === 'bee' ? '🐝 Con ong' : role === 'tiger' ? '🐯 Con hổ' : '👫 Đoàn người'}</p>
        {role === 'chain' && <p className="text-primary-brown-light mb-4">Đoàn người còn {chainLength} người</p>}
        <div className="bg-white rounded-2xl px-10 py-6 text-center mb-8">
          <p className="text-sm text-primary-brown-light mb-1">Điểm đạt được</p>
          <p className="text-4xl font-extrabold text-primary-orange">{score}</p>
        </div>
        <div className="flex gap-4">
          {!saved ? (
            <button onClick={handleSave} className="px-7 py-3 bg-gradient-to-r from-green-500 to-emerald-400 text-white font-bold rounded-2xl cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-trophy-fill"></i> Lưu thành tích
            </button>
          ) : (
            <div className="px-7 py-3 bg-green-100 text-green-700 font-bold rounded-2xl flex items-center gap-2">
              <i className="ri-check-fill"></i> Đã lưu!
            </div>
          )}
          <button onClick={onBack} className="px-7 py-3 bg-gradient-to-r from-primary-orange to-primary-yellow text-white font-bold rounded-2xl cursor-pointer whitespace-nowrap">
            ← Quay lại
          </button>
        </div>
      </div>
    );
  }

  const characterEmoji = currentSituation.character === 'bee' ? '🐝' : '🐯';
  const characterName = currentSituation.character === 'bee' ? 'Con ong' : 'Con hổ';
  const characterColor = currentSituation.character === 'bee' ? 'amber' : 'orange';

  return (
    <div className="min-h-screen bg-primary-cream">
      <div className="max-w-2xl mx-auto px-6 pt-8 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="bg-white rounded-2xl px-4 py-2 text-center">
            <p className="text-xs text-primary-brown-light">Vai của bạn</p>
            <p className="text-lg font-extrabold text-primary-brown-dark">
              {role === 'bee' ? '🐝 Con ong' : role === 'tiger' ? '🐯 Con hổ' : '👫 Đoàn người'}
            </p>
          </div>
          <div className="bg-white rounded-2xl px-4 py-2 flex items-center gap-2">
            <i className="ri-trophy-fill text-primary-orange"></i>
            <p className="text-lg font-extrabold text-primary-orange">{score} điểm</p>
          </div>
          {role === 'chain' && (
            <div className="bg-sky-50 rounded-2xl px-4 py-2 text-center border border-sky-200">
              <p className="text-xs text-sky-500">Đoàn người</p>
              <p className="text-lg font-extrabold text-sky-700">{chainLength} người</p>
            </div>
          )}
        </div>

        {/* Chain visualization */}
        {role === 'chain' && (
          <div className="flex items-center justify-center gap-1 mb-5 flex-wrap bg-white rounded-2xl py-4 px-6">
            {Array.from({ length: chainLength }).map((_, i) => (
              <span key={i} className="text-2xl">{i === 0 ? '🧑' : i === chainLength - 1 ? '🙋' : '🧒'}</span>
            ))}
            <span className="text-2xl ml-2">{currentSituation.character === 'bee' ? '🐝' : '🐯'}</span>
          </div>
        )}

        {/* Current situation */}
        <div className={`rounded-3xl p-6 mb-5 ${characterColor === 'amber' ? 'bg-amber-50 border-2 border-amber-200' : 'bg-orange-50 border-2 border-orange-200'}`}>
          <div className="flex items-center gap-3 mb-4">
            <span className="text-5xl">{characterEmoji}</span>
            <div>
              <h3 className={`text-xl font-extrabold ${characterColor === 'amber' ? 'text-amber-700' : 'text-orange-700'}`}>{characterName}</h3>
              <p className="text-xs text-primary-brown-light">Tình huống {situationIdx + 1}/{SITUATIONS.length}</p>
            </div>
          </div>
          <p className="text-base font-bold text-primary-brown-dark leading-relaxed">{currentSituation.question}</p>
        </div>

        {/* Answer options */}
        <div className="space-y-3">
          {currentSituation.options.map((opt, idx) => {
            let cls = 'border-2 border-primary-brown-light/20 bg-white text-primary-brown hover:border-primary-orange hover:bg-primary-orange/5';
            if (answered) {
              if (idx === currentSituation.correct) cls = 'border-2 border-green-400 bg-green-50 text-green-700 font-bold';
              else if (idx === selectedAnswer) cls = 'border-2 border-red-400 bg-red-50 text-red-600';
              else cls = 'border-2 border-transparent bg-white/50 text-primary-brown-light';
            }
            return (
              <button key={idx} onClick={() => handleAnswer(idx)} disabled={answered}
                className={`w-full text-left px-5 py-3.5 rounded-2xl text-base transition-all cursor-pointer ${cls}`}>
                <span className="font-bold mr-2">{String.fromCharCode(65+idx)}.</span>{opt}
              </button>
            );
          })}
        </div>

        {feedback && (
          <div className={`mt-4 p-4 rounded-2xl text-base font-semibold ${selectedAnswer === currentSituation.correct ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
            {feedback}
          </div>
        )}
      </div>
    </div>
  );
}
